import Vue from "vue";
import Router from "vue-router";
import {
  CURRENT_SCHEDULE_YEAR,
  SCHEDULE_ALIAS_PATH,
  SCHEDULE_CANONICAL_PATH,
  SCHEDULE_YEARS,
  schedulePathForYear,
  scheduleYearForPath,
} from "./config/scheduleArchive";
import {
  scheduleEventCatalog,
  scheduleEventCatalogEntryForPath,
} from "./config/scheduleEventCatalog";
import siteMetadata from "./config/siteMetadata.json";
import { updateDocumentMetadata } from "./head";
import { setDeferredScheduleHashScroll } from "./scheduleHashScroll";

Vue.use(Router);

const ScheduleView = () =>
  import(/* webpackChunkName: "schedule" */ "./views/Schedule.vue");
const schedulePageComponents = SCHEDULE_YEARS.reduce((components, year) => {
  components[schedulePathForYear(year)] = ScheduleView;
  return components;
}, {});
const ScheduleEventView = () =>
  import(/* webpackChunkName: "schedule-event" */ "./views/ScheduleEvent.vue");
const scheduleEventPageComponents = scheduleEventCatalog.reduce(
  (components, event) => {
    components[event.path] = ScheduleEventView;
    return components;
  },
  {}
);
const VideoWatchView = () =>
  import(/* webpackChunkName: "video-watch" */ "./views/VideoWatch.vue");
const videoWatchPageComponents = Object.entries(siteMetadata.pages).reduce(
  (components, [routePath, page]) => {
    if (Object.prototype.hasOwnProperty.call(page, "videoId")) {
      components[routePath] = VideoWatchView;
    }

    return components;
  },
  {}
);

const pageComponents = {
  "/": () => import(/* webpackChunkName: "home" */ "./views/Home.vue"),
  "/news": () =>
    import(/* webpackChunkName: "news" */ "./views/News.vue"),
  ...schedulePageComponents,
  ...scheduleEventPageComponents,
  "/profile": () =>
    import(/* webpackChunkName: "profile" */ "./views/Profile.vue"),
  "/music": () =>
    import(/* webpackChunkName: "music" */ "./views/Music.vue"),
  "/videos": () =>
    import(/* webpackChunkName: "videos" */ "./views/Video.vue"),
  ...videoWatchPageComponents,
  "/contact": () =>
    import(/* webpackChunkName: "contact" */ "./views/Contact.vue"),
};

const NotFound = () =>
  import(/* webpackChunkName: "not-found" */ "./views/NotFound.vue");

if (!siteMetadata.pages || typeof siteMetadata.pages !== "object") {
  throw new Error("Missing page metadata collection");
}

const pageEntries = Object.entries(siteMetadata.pages);
const metadataPaths = pageEntries.map(([path]) => path);
const componentPaths = Object.keys(pageComponents);
const expectedSchedulePaths = SCHEDULE_YEARS.map(schedulePathForYear);
const metadataSchedulePaths = metadataPaths.filter(
  (path) => scheduleYearForPath(path) !== null
);
const missingComponents = metadataPaths.filter(
  (path) => !Object.prototype.hasOwnProperty.call(pageComponents, path)
);
const missingMetadata = componentPaths.filter(
  (path) => !Object.prototype.hasOwnProperty.call(siteMetadata.pages, path)
);

if (missingComponents.length || missingMetadata.length) {
  throw new Error(
    `Route metadata/component mismatch. Missing components: ${
      missingComponents.join(", ") || "none"
    }. Missing metadata: ${missingMetadata.join(", ") || "none"}.`
  );
}

if (
  Object.prototype.hasOwnProperty.call(siteMetadata.pages, SCHEDULE_ALIAS_PATH) ||
  metadataSchedulePaths.length !== expectedSchedulePaths.length ||
  metadataSchedulePaths.some(
    (path, index) => path !== expectedSchedulePaths[index]
  )
) {
  throw new Error(
    "Schedule metadata must contain only the configured canonical year routes."
  );
}

const validateMetadata = (label, metadata) => {
  const hasRequiredText =
    metadata &&
    ["title", "description"].every(
      (field) =>
        typeof metadata[field] === "string" && metadata[field].trim().length
    );

  if (!hasRequiredText) {
    throw new Error(`Missing title or description for route metadata: ${label}`);
  }

  if (
    Object.prototype.hasOwnProperty.call(metadata, "robots") &&
    (typeof metadata.robots !== "string" || !metadata.robots.trim())
  ) {
    throw new Error(`Invalid robots directive for route metadata: ${label}`);
  }
};

pageEntries.forEach(([path, metadata]) => {
  if (
    !path.startsWith("/") ||
    path.includes("?") ||
    path.includes("#") ||
    (path !== "/" && path.endsWith("/"))
  ) {
    throw new Error(`Route metadata path must be canonical: ${path}`);
  }
  validateMetadata(path, metadata);

  const scheduleYear = scheduleYearForPath(path);
  const scheduleEvent = scheduleEventCatalogEntryForPath(path);
  const configuredScheduleYear = scheduleEvent
    ? scheduleEvent.year
    : scheduleYear;
  const hasScheduleItemId = Object.prototype.hasOwnProperty.call(
    metadata,
    "scheduleItemId"
  );
  if (
    (Object.prototype.hasOwnProperty.call(
      metadata,
      "scheduleStructuredData"
    ) && typeof metadata.scheduleStructuredData !== "boolean") ||
    (configuredScheduleYear === null &&
      Object.prototype.hasOwnProperty.call(metadata, "scheduleYear")) ||
    (configuredScheduleYear !== null &&
      metadata.scheduleYear !== configuredScheduleYear) ||
    (scheduleEvent &&
      metadata.scheduleItemId !== scheduleEvent.scheduleItemId) ||
    (!scheduleEvent && hasScheduleItemId) ||
    (metadata.scheduleStructuredData &&
      (configuredScheduleYear === null ||
        configuredScheduleYear < CURRENT_SCHEDULE_YEAR))
  ) {
    throw new Error(`Invalid schedule metadata for route: ${path}`);
  }
});
validateMetadata("404", siteMetadata.notFound);

if (
  !siteMetadata.site ||
  typeof siteMetadata.site.robots !== "string" ||
  !siteMetadata.site.robots.trim()
) {
  throw new Error("Missing default robots directive in site metadata");
}

if (
  typeof siteMetadata.notFound.robots !== "string" ||
  !siteMetadata.notFound.robots.trim()
) {
  throw new Error("Missing robots directive for 404 route metadata");
}

const routes = pageEntries.map(([path, metadata]) => ({
  path,
  component: pageComponents[path],
  meta: {
    ...metadata,
    robots: metadata.robots || siteMetadata.site.robots,
    canonicalPath: path,
  },
}));

routes.push({
  path: SCHEDULE_ALIAS_PATH,
  redirect: SCHEDULE_CANONICAL_PATH,
});

routes.push({
  path: "*",
  component: NotFound,
  meta: { ...siteMetadata.notFound },
});

const router = new Router({
  mode: "history",
  scrollBehavior: (to, from, savedPosition) => {
    setDeferredScheduleHashScroll(
      to.fullPath,
      Boolean(
        !savedPosition &&
        to.hash &&
        Number.isInteger(to.meta.scheduleYear)
      )
    );

    if (savedPosition) {
      return savedPosition;
    }
    if (to.hash) {
      return {
        selector: to.hash,
        offset: {
          x: 0,
          y: 80,
        },
      };
    }
    return { x: 0, y: 0 };
  },
  routes,
});

router.afterEach((to) => {
  updateDocumentMetadata(to);
});

export default router;
